import React from 'react';
import { Box, Link, Skeleton, Typography } from '@mui/material';
import PanelCard from '../../design/PanelCard';
import { formatShortDate } from '../../design/format';
import { NewsItem } from '../../hooks/useUniverse';

/** Shape of the useStockNews hook result. */
export interface NewsState {
  data: { ticker: string; items: NewsItem[] } | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const secondaryLine = (item: NewsItem): string | null => {
  const parts: string[] = [];
  if (item.publisher) parts.push(item.publisher);
  if (item.published_at) parts.push(formatShortDate(item.published_at));
  return parts.length > 0 ? parts.join(' • ') : null;
};

/** Feed content is external — everything renders as plain text nodes, and only http(s) links become anchors. */
const NewsPanel: React.FC<{ news: NewsState }> = ({ news }) => {
  const items = news.data?.items ?? [];
  return (
    <PanelCard title="Recent news" error={news.error} onRetry={news.refetch}>
      {news.loading ? (
        <Skeleton variant="rounded" height={160} />
      ) : items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No recent news
        </Typography>
      ) : (
        items.map((item, index) => (
          <Box
            key={`${item.link}-${index}`}
            sx={{ py: 1, '& + &': { borderTop: '1px solid rgba(15, 23, 42, 0.07)' } }}
          >
            {/^https?:\/\//.test(item.link) ? (
              <Link href={item.link} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ fontWeight: 600 }}>
                {item.title}
              </Link>
            ) : (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.title}</Typography>
            )}
            {secondaryLine(item) && (
              <Typography variant="caption" color="text.secondary" component="div">
                {secondaryLine(item)}
              </Typography>
            )}
            {item.summary && (
              <Typography
                variant="body2" color="text.secondary"
                sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                {item.summary}
              </Typography>
            )}
          </Box>
        ))
      )}
    </PanelCard>
  );
};

export default NewsPanel;
